import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { Circle } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Touch-friendly radio group components with:
 * - Adequate spacing between options (minimum 8px)
 * - Extended touch targets (44x44px minimum per WCAG 2.5.5)
 * - Visual feedback for touch interactions
 */

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn(
        // Increased gap from gap-2 to gap-3 for better touch separation
        "grid gap-3",
        className
      )}
      {...props}
      ref={ref}
    />
  )
})
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        // Base styles - increased from h-4 w-4 to h-5 w-5 for better visibility
        "aspect-square h-5 w-5 rounded-full border border-primary text-primary",
        // Focus styles
        "ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        // Disabled states
        "disabled:cursor-not-allowed disabled:opacity-50",
        // Touch-friendly enhancements
        "touch-action-manipulation select-none",
        // Extended touch target using relative positioning
        "relative",
        // Active state feedback
        "active:scale-95 transition-transform duration-100",
        className
      )}
      {...props}
    >
      {/* Extended touch target - invisible but captures touch/click */}
      <span
        className="absolute"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: '44px',
          minHeight: '44px',
          width: '44px',
          height: '44px',
        }}
      />
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Circle className="h-2.5 w-2.5 fill-current text-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
})
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export { RadioGroup, RadioGroupItem }
